import React, { useEffect, useState } from "react";
import {
  FileText,
  Calendar,
  Newspaper,
  Clock,
  CheckCircle,
  AlertCircle,
  X,
} from "lucide-react";

import { useLanguage } from "@/contexts/LanguageContext";
import { useUserInfo } from "@/hooks/useUserInfo";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { StatusBadge } from "@/components/ui/status-badge";
import { MobileAppLaunchers } from "@/components/layout/AppLaunchers";
import { WelcomeHeader } from "@/components/WelcomeHeader";
import AuthDebugPanel from "@/components/debug/AuthDebugPanel";
import { getSummary } from "@/utils/pasUtils.js";
import { useNavigate } from "react-router-dom";

const Dashboard: React.FC = () => {
  const VITE_USERNAMET = import.meta.env.VITE_USERNAMET
  const VITE_PASSWORD = import.meta.env.VITE_PASSWORD

  const { t } = useLanguage();
  const { greeting, fullName, email, role, profilePictureUrl } = useUserInfo();

  const quickStats = [
    {
      title: t("dashboard.pending_requests"),
      value: "0",
      icon: Clock,
      color: "text-warning",
      bgColor: "bg-warning/10",
    },
    {
      title: t("dashboard.completed_requests"),
      value: "0",
      icon: CheckCircle,
      color: "text-success",
      bgColor: "bg-success/10",
    },
    {
      title: t("dashboard.monthly_events"),
      value: "0",
      icon: Calendar,
      color: "text-primary",
      bgColor: "bg-primary/10",
    },
    {
      title: t("dashboard.new_notifications"),
      value: "0",
      icon: AlertCircle,
      color: "text-destructive",
      bgColor: "bg-destructive/10",
    },
  ];

  const latestNews = [
    {
      id: 1,
      title: t("dashboard.news.pilot_launch"),
      date: "2025-10-01",
      category: t("dashboard.news.tech_category"),
    },
    {
      id: 2,
      title: t("dashboard.news.national_day"),
      date: "2025-09-23",
      category: t("dashboard.news.general_category"),
    },
  ];

  const [summary, setSummary] = useState({ attendance: [], tasks: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const navigate = useNavigate();

  useEffect(() => {
    async function fetchAttendance() {
      try {
        setLoading(true);
        setError("");

        const summaryResponse = await getSummary(
          VITE_USERNAMET,
          VITE_PASSWORD,
          email
        );

        console.log(summaryResponse);

        setSummary(summaryResponse);
      } catch (err) {
        setError(err.message || "Failed to fetch attendance");
      } finally {
        setLoading(false);
      }
    }

    fetchAttendance();
  }, []);

  const attendanceStatus = (status: string) =>
    status === "Present"
      ? "approved"
      : status === "Absent"
      ? "rejected"
      : status === "On Leave"
      ? "in_progress"
      : "draft";

  const formatTime = (value: string) =>
    new Date(value).toLocaleTimeString("ar-SA-u-nu-latn", {
      hour: "2-digit",
      minute: "2-digit",
    });

  const rowSkeleton = (
    <div className="rows animate-pulse">
      {[1, 2, 3].map((i) => (
        <div key={i} className="flex items-center gap-4 py-5">
          <div className="flex-1 space-y-2">
            <div className="h-4 w-1/3 rounded bg-muted"></div>
            <div className="h-3 w-2/3 rounded bg-muted"></div>
          </div>
          <div className="h-6 w-14 rounded-full bg-muted"></div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="space-y-6 sm:space-y-8 fade-in">
      {/* Welcome Section */}
      <WelcomeHeader />

      {/* App launchers - mobile only (shown in the header on desktop) */}
      <MobileAppLaunchers />

      {/* Quick Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px overflow-hidden rounded-[24px] border border-border/60 bg-border/60">
        {quickStats.map((stat, index) => (
          <div key={index} className="bg-card p-5 sm:p-6">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-muted-foreground">{stat.title}</p>
              <div className={`shrink-0 rounded-full p-2 ${stat.bgColor}`}>
                <stat.icon className={`h-4 w-4 ${stat.color}`} />
              </div>
            </div>
            <p className="mt-3 text-4xl sm:text-5xl font-extrabold tabular-nums">
              {stat.value}
            </p>
          </div>
        ))}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Attendance Records */}
        <section className="surface pb-2">
          <div className="surface-head">
            <h2 className="surface-title">
              <Clock className="h-5 w-5 text-primary" />
              {t("dashboard.attendance_record")}
            </h2>
            <Button
              onClick={() => navigate("/attendance")}
              variant="ghost"
              size="sm"
              className="text-primary hover:text-primary"
            >
              {t("dashboard.view_full_attendance")}
            </Button>
          </div>

          {loading && rowSkeleton}
          {error && (
            <div className="text-center py-8">
              <p className="text-destructive">{error}</p>
            </div>
          )}

          <div className="rows">
            {summary.attendance.map((record, index) => (
              <div
                key={index}
                className="grid grid-cols-[1fr_auto] sm:grid-cols-[9rem_1fr_auto] items-center gap-x-4 gap-y-3 py-4"
              >
                <div className="min-w-0">
                  <p className="text-xs text-muted-foreground">
                    {new Date(record.attendance_date).toLocaleDateString(
                      "ar-SA",
                      { weekday: "long" }
                    )}
                  </p>
                  <p className="font-bold tabular-nums">
                    {new Date(record.attendance_date).toLocaleDateString(
                      "en-Us",
                      { year: "numeric", month: "2-digit", day: "2-digit" }
                    )}
                  </p>
                </div>

                <div className="col-span-2 sm:col-span-1 order-last sm:order-none">
                  {record.status === "Present" && (
                    <div className="grid grid-cols-3 gap-3">
                      <div>
                        <p className="kv-label">{t("attendance.in_time")}</p>
                        <p className="kv-value">{formatTime(record.in_time)}</p>
                      </div>
                      <div>
                        <p className="kv-label">{t("attendance.out_time")}</p>
                        <p
                          className={`kv-value ${
                            !record.out_time ? "text-destructive/70" : ""
                          }`}
                        >
                          {record.out_time
                            ? formatTime(record.out_time)
                            : t("attendance.no_checkout")}
                        </p>
                      </div>
                      <div>
                        <p className="kv-label">{t("attendance.total_time")}</p>
                        <p className="kv-value text-primary">
                          {record.total_time
                            ? record.total_time.split(".")[0]
                            : t("common.not_calculated")}
                        </p>
                      </div>
                    </div>
                  )}
                </div>

                <StatusBadge status={attendanceStatus(record.status)}>
                  {record.status === "Present" && t("status.present")}
                  {record.status === "Holiday" && t("status.holiday")}
                  {record.status === "On Leave" && t("status.on_leave")}
                  {record.status === "Absent" && t("status.absent")}
                </StatusBadge>
              </div>
            ))}
          </div>

          {summary.attendance.length === 0 && !loading && !error && (
            <div className="text-center py-10">
              <Clock className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
              <p className="text-muted-foreground">{t("dashboard.no_records")}</p>
            </div>
          )}
        </section>

        {/* Tasks Section */}
        <section className="surface pb-2">
          <div className="surface-head">
            <h2 className="surface-title">
              <FileText className="h-5 w-5 text-primary" />
              {t("nav.my_tasks")}
            </h2>
            {!loading && !error && summary.tasks.length > 0 && (
              <Button
                onClick={() => navigate("/tasks")}
                variant="ghost"
                size="sm"
                className="text-primary hover:text-primary"
              >
                {t("tasks.view_all")}
              </Button>
            )}
          </div>

          {loading && rowSkeleton}
          {error && (
            <div className="text-center py-8">
              <AlertCircle className="h-10 w-10 text-destructive mx-auto mb-3" />
              <p className="text-destructive font-medium">{error}</p>
            </div>
          )}

          <div className="rows">
            {!loading &&
              !error &&
              summary.tasks.map((task) => (
                <div key={task.task_id} className="flex items-start gap-4 py-4">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-muted-foreground mb-1">
                      #{task.task_id}
                    </p>
                    <h4 className="font-bold leading-snug break-words">
                      {task.task_subject}
                    </h4>
                    <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {t("tasks.start_date")} {task.exp_start_date}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Calendar className="h-3 w-3" />
                        {t("tasks.end_date")} {task.exp_start_date}
                      </span>
                    </div>
                  </div>
                  <StatusBadge status="in_progress" className="text-xs shrink-0">
                    {t("tasks." + task.task_status?.toLowerCase())}
                  </StatusBadge>
                </div>
              ))}
          </div>

          {!loading && !error && summary.tasks.length === 0 && (
            <div className="text-center py-12">
              <div className="w-14 h-14 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                <FileText className="h-7 w-7 text-muted-foreground" />
              </div>
              <h3 className="font-bold mb-1">{t("tasks.no_tasks")}</h3>
              <p className="text-muted-foreground text-sm">
                {t("tasks.upcoming_tasks")}
              </p>
            </div>
          )}
        </section>
      </div>

      {/* Latest News */}
      <section className="space-y-4">
        <h2 className="surface-title px-1">
          <Newspaper className="h-5 w-5 text-primary" />
          {t("profile.latest_news")}
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {latestNews.map((news) => (
            <article
              key={news.id}
              className="surface group p-5 transition-all hover:-translate-y-0.5 hover:shadow-[var(--shadow-md)]"
            >
              <span className="inline-block px-3 py-1 bg-primary/10 text-primary text-xs font-medium rounded-full">
                {news.category}
              </span>
              <h4 className="mt-4 font-bold leading-snug line-clamp-2 group-hover:text-primary transition-colors">
                {news.title}
              </h4>
              <p className="mt-2 text-sm text-muted-foreground">{news.date}</p>
            </article>
          ))}
        </div>
      </section>
    </div>
  );
};

export default Dashboard;
